<template>
  <b-form-group
    label-cols="12"
    label-cols-lg="2"
    label-size="sm"
    :label="subject.title"
    :label-for="subject.id"
    class="collection-select"
    :class="{ 'collection-select--disabled': computedDisabled }"
  >
    <div class="collection-select__loading-wrapper" v-if="isFetchingData">
      <b-spinner style="border-width: 2px" />
    </div>
    <template v-else>
      <AppSingleSelect
        v-if="presentationType === 'select'"
        :id="subject.id"
        v-model="selectedSelectValue"
        :options="optionsWithPlaceholder"
        :placeholder="placeholder"
        :disabled="subject.disabled"
        :has-error="validateError"
        @input="handleSelectValueChanged"
      />
      <b-form-radio-group
        v-if="presentationType === 'radio'"
        :id="subject.id"
        v-model="selectedRadioValue"
        :options="options"
        :disabled="subject.disabled"
        @input="handleRadioValueChanged"
      ></b-form-radio-group>
      <b-form-checkbox-group
        v-if="presentationType === 'checkbox'"
        :id="subject.id"
        v-model="selectedCheckboxValue"
        @input="handleCheckboxValueChanged"
      >
        <b-form-checkbox
          v-for="(option, optionIndex) in options"
          :key="optionIndex"
          :value="optionIndex.toString()"
          :disabled="subject.disabled"
        >
          {{ option.text }}
        </b-form-checkbox>
      </b-form-checkbox-group>
    </template>
    <small v-if="subject.memo" class="form-text text-muted">
      {{ subject.memo }}
    </small>
    <b-form-invalid-feedback :state="!validateError">
      輸入的資料有誤或未填寫，請確認
    </b-form-invalid-feedback>
  </b-form-group>
</template>

<script>
import collectionApi from "@/apis/collection";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import Collection from "@/models/Collection.model.js";
import DynamicFormValuePresenter from "@/presenters/DynamicFormValue.presenter"
import _ from "lodash";

export default {
  components: { AppSingleSelect },
  props: {
    subject: {
      type: Object,
      required: true,
    },
    // value: [String, Number, Array, Object],
    value: {
      type: Array,
      default: () => {[]},
    },
    validateError: {
      type: Boolean,
    },
    placeholder: {
      type: String,
      default: '請選擇',
    },
    branchId: {
      type: String,
      required: false,
    },
    isRelatedToBranch: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      collection: null,
      isFetchingData: false,
      selectedSelectValue: null,
      selectedRadioValue: null,
      selectedCheckboxValue: [],
    }
  },
  computed: {
    selected: {
      get() {
        // NOTE: parse array value to a single value, also treat undefined as null, to make placeholder work as expected
        if (Array.isArray(this.value)) {
          return this.value.map(v => v === undefined ? null : v)
        }
        return this.value === undefined ? null : this.value;
      },
      set(v) {
        if (v !== null) {
          this.$emit("input", Array.isArray(v) ? v : [v]);
        } else {
          this.$emit("input", null);
        }
      }
    },
    options() {
      if (this.collection === null) {
        return []
      }

      const collectionModel = new Collection(this.collection)

      return [
        ..._.get(this.collection, 'items', []).sort((a, b) => a.order - b.order).map((item) => {
          return {
            value: DynamicFormValuePresenter.collectionItem(this.subject, item, this.collection),
            text: collectionModel.textForOption(item.id),
          };
        })
      ];
    },
    optionsWithPlaceholder() {
      return [
        { text: this.placeholder, value: null },
        ...this.options
      ];
    },
    computedDisabled() {
      return this.disabled || (this.isRelatedToBranch && !this.branchId)
    },
    presentationType() {
      return this.subject?.collection_presentation || 'select'
    },
  },
  watch: {
    selected() {
      this.updateSelectedVmodels()
    },
    options() {
      this.updateSelectedVmodels()
    },
  },
  methods: {
    updateSelectedVmodels() {
      if (this.selected === null) {
        this.selectedSelectValue = null
        this.selectedRadioValue = null
        this.selectedCheckboxValue = []
      } else if (Array.isArray(this.selected)) {
        this.selectedSelectValue = this.selected[0]
        this.selectedRadioValue = this.selected[0]
        this.selectedCheckboxValue = this.options
          .map((option, index) => index.toString()) // eslint-disable-line no-unused-vars
          .filter((optionIndex) => this.selected.some((value) => _.isEqual(value, this.options[optionIndex].value)))
      } else {
        this.selectedSelectValue = this.selected
        this.selectedRadioValue = this.selected
        this.selectedCheckboxValue = [this.options.findIndex((option) => _.isEqual(option.value, this.selected)).toString()]
      }
    },
    handleSelectValueChanged(option) {
      if (option.value === null) {
        this.selected = []
      } else {
        this.selected = [option.value]
      }
    },
    handleRadioValueChanged(value) {
      if (value === null) {
        this.selected = []
      } else {
        this.selected = [value]
      }
    },
    handleCheckboxValueChanged() {
      this.selected = this.options
        .filter((option, index) => this.selectedCheckboxValue.includes(index.toString()))
        .map(option => option.value)
    },
    async fetchCollectionItems() {
      this.isFetchingData = true
      try {
        if (this.isRelatedToBranch) {
          if (this.branchId) {
            const response = await collectionApi.getOptionsForBranch(this.subject.collection_id, this.branchId);
            this.collection = response.data.data;
          } else {
            this.collection = null
          }
        } else {
          const response = await collectionApi.getCollection(this.subject.collection_id);
          this.collection = response.data.data;
        }
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", `讀取 ${this.subject.title} 選項時發生錯誤，請重新整理後再試一次`, "error");
      } finally {
        this.isFetchingData = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.collection-select {
  &__loading-wrapper {
    height: calc(1.5em + 0.75rem + 2px);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ced4da;
    border-radius: 3px;
  }

  &--disabled {
    cursor: not-allowed;
  }
}

::v-deep.collection-select--disabled select.custom-select {
  border: 1px solid #e5e5ea !important;
  background-color: #eee;
  color: #888;
  pointer-events: none;
}
</style>
